'use client'

import React from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Package, Sparkles, Plus } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { InventoryItem } from '@/lib/api/silo-api/types'
import { cn } from '@/lib/utils'

interface ProductListItemProps {
  product: InventoryItem
  isSelected: boolean
  onSelect: (product: InventoryItem) => void
  onAddProduct?: (product: InventoryItem) => void
}

export default function ProductListItem({ product, isSelected, onSelect, onAddProduct }: ProductListItemProps) {
  // Get CBD and primary terpene percentages
  const getCBDPercentage = () => {
    if (!product.cannabinoids || product.cannabinoids.length === 0) return null
    
    const cbd = product.cannabinoids.find(c => 
      c.cannabinoid?.abbreviation?.toUpperCase() === 'CBD' ||
      c.cannabinoid?.name?.toUpperCase().includes('CBD')
    )
    
    return cbd ? parseFloat(cbd.percentage).toFixed(1) : null
  }

  const getTopTerpene = () => {
    if (!product.terpenes || product.terpenes.length === 0) return null
    
    const topTerpene = product.terpenes
      .filter(t => t.terpene && t.percentage)
      .sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage))[0]
    
    return topTerpene ? {
      name: topTerpene.terpene.name,
      percentage: parseFloat(topTerpene.percentage).toFixed(2)
    } : null
  }

  const cbdPercentage = getCBDPercentage()
  const topTerpene = getTopTerpene()
  const isLiveResin = product.name.toLowerCase().includes('live resin')

  return (
    <div
      onClick={() => onSelect(product)}
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200",
        "hover:bg-white/5 hover:shadow-elev-1",
        isSelected ? "bg-purple-500/10 border border-purple-500/30" : "bg-gray-800/20 border border-white/5"
      )}
    >
      {/* Product Image/Avatar */}
      <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-800/50 flex items-center justify-center">
        {product.image_url ? (
          <img 
            src={product.image_url} 
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Package className="w-6 h-6 text-white/30" />
        )}
      </div>

      {/* Product Info */}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-white truncate">
          {product.name}
        </h4>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          {isLiveResin && (
            <Badge 
              variant="secondary" 
              className="text-xs bg-yellow-500/20 text-yellow-300 border-yellow-500/30"
            >
              <Sparkles className="w-3 h-3 mr-0.5" />
              Live Resin
            </Badge>
          )}
          {product.product_type?.name && (
            <Badge 
              variant="secondary" 
              className="text-xs bg-blue-500/20 text-blue-300 border-blue-500/30"
            >
              {product.product_type.name}
            </Badge>
          )}
          {cbdPercentage && (
            <Badge 
              variant="secondary" 
              className="text-xs bg-purple-500/20 text-purple-300 border-purple-500/30"
            >
              CBD: {cbdPercentage}%
            </Badge>
          )}
          {topTerpene && (
            <Badge 
              variant="secondary" 
              className="text-xs bg-green-500/20 text-green-300 border-green-500/30"
            >
              {topTerpene.name}
            </Badge>
          )}
        </div>
      </div>

      {/* Sample Order Actions */}
      <div className="flex-shrink-0 flex items-center gap-2">
        <div className="text-xs text-white/60 text-right">
          <div>Available for</div>
          <div className="text-eskerium-gold font-medium">Sample Request</div>
        </div>
        {onAddProduct && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    onAddProduct(product)
                  }}
                  size="sm"
                  className="h-8 w-8 p-0 bg-eskerium-gold/20 hover:bg-eskerium-gold/30 border border-eskerium-gold/40 text-eskerium-gold hover:text-white"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Request Sample</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  )
}